import { Card, CardContent, Typography, Box, Tooltip } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { tokens } from '../../../theme'

const countUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

/**
 * ComparisonCard — Side-by-side before/after values
 * Use for metrics with a clear directional change (e.g., kW/Ton reduction)
 */
const ComparisonCard = ({
  title,
  beforeValue,
  afterValue,
  beforeLabel = 'Before',
  afterLabel = 'After',
  changeValue,
  changeLabel,
  subtitle,
  icon,
  color = '#4ECDC4',
  onClick
}) => {
  const theme = useTheme()
  const palette = theme.palette

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.3),
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(color, 0.1)}`
        } : {
          borderColor: alpha(palette.text.primary, 0.1)
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.4)} 100%)`,
          opacity: 0.8
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: '16px !important', sm: '20px !important' } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                lineHeight: 1.5,
                display: 'block',
                mb: 1
              }}
            >
              {title}
            </Typography>

            {/* Before → After comparison */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    color: 'text.secondary',
                    lineHeight: 1,
                    opacity: 0.7
                  }}
                >
                  {beforeValue}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mt: 0.25 }}
                >
                  {beforeLabel}
                </Typography>
              </Box>

              {/* Arrow */}
              <Typography
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.85rem',
                  mx: 0.25,
                  mt: -1
                }}
              >
                →
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    color: color,
                    lineHeight: 1
                  }}
                >
                  {afterValue}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mt: 0.25 }}
                >
                  {afterLabel}
                </Typography>
              </Box>
            </Box>

            {/* Change badge */}
            {changeValue && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: alpha(color, 0.1),
                  border: `1px solid ${alpha(color, 0.2)}`,
                  borderRadius: tokens.radius.xs,
                  px: 1,
                  py: 0.25,
                  mb: 0.5,
                  animation: `${countUp} 0.5s ease-out`
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    color: color,
                    lineHeight: 1
                  }}
                >
                  {changeValue}
                </Typography>
                {changeLabel && (
                  <Typography
                    sx={{
                      color: alpha(color, 0.8),
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  >
                    {changeLabel}
                  </Typography>
                )}
              </Box>
            )}

            {/* Subtitle */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.75,
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Icon Badge */}
          {icon && (
            <Box
              sx={{
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                borderRadius: tokens.radius.sm,
                p: { xs: 0.75, sm: 1, md: 1.25 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 16px ${alpha(color, 0.3)}`,
                transition: `all ${tokens.transitions.normal}`,
                ml: { xs: 1, sm: 1.5 },
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  color: palette.primary.contrastText,
                  fontSize: { xs: 18, sm: 20, md: 24 }
                }
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default ComparisonCard
